/**
 * Dev widget — a self-contained, framework-agnostic overlay rendered with plain DOM
 * (Shadow DOM so host styles can't leak in/out). Bottom-right corner.
 *
 * Shows: total / errors / unknown domains, recent requests, and actions:
 * open details, export HAR, export JSON, "mark domain as allowed".
 */
import type { RequestEvent } from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';
import { store } from './store.js';

const STYLES = `
:host { all: initial; }
.wrap { position: fixed; right: 16px; bottom: 16px; z-index: 2147483000;
  font: 12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; color: #e6e6e6; }
.badge { display: flex; gap: 10px; align-items: center; background: #0b0b0f;
  border: 1px solid #26263a; border-radius: 10px; padding: 8px 12px; cursor: pointer;
  box-shadow: 0 8px 30px rgba(0,0,0,.45); }
.dot { width: 7px; height: 7px; border-radius: 50%; background: #34d399; }
.dot.err { background: #f87171; }
.stat b { color: #fff; }
.stat .muted { color: #8a8aa3; }
.panel { width: 380px; max-height: 460px; overflow: auto; margin-top: 8px;
  background: #0b0b0f; border: 1px solid #26263a; border-radius: 12px; display: none; }
.panel.open { display: block; }
.head { display: flex; justify-content: space-between; align-items: center;
  padding: 10px 12px; border-bottom: 1px solid #1c1c2b; position: sticky; top: 0; background: #0b0b0f; }
.title { color: #fff; font-weight: 600; }
.btns { display: flex; gap: 6px; }
button { all: unset; cursor: pointer; padding: 4px 8px; border-radius: 6px;
  border: 1px solid #2c2c44; color: #c7c7e0; font-size: 11px; }
button:hover { background: #16162a; }
.row { padding: 8px 12px; border-bottom: 1px solid #15151f; display: grid;
  grid-template-columns: 46px 1fr auto; gap: 8px; align-items: center; }
.row:hover { background: #101020; }
.m { color: #93c5fd; font-weight: 600; }
.u { color: #d6d6e7; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.s { font-weight: 600; }
.s.ok { color: #34d399; } .s.warn { color: #fbbf24; } .s.err { color: #f87171; }
.tag { display: inline-block; font-size: 10px; padding: 0 5px; border-radius: 4px;
  margin-left: 6px; border: 1px solid #4b3a12; color: #fbbf24; }
.tag.unknown { border-color: #5b1f1f; color: #f87171; }
.detail { grid-column: 1 / -1; color: #9aa; font-size: 11px; padding-top: 6px; display: none; }
.detail.open { display: block; }
.detail code { color: #c7c7e0; }
.allow { border-color: #1f5b3a; color: #34d399; }
`;

export function mountWidget(cfg: ResolvedConfig): () => void {
  if (typeof document === 'undefined') return () => {};

  const host = document.createElement('div');
  host.setAttribute('data-aris-widget', '');
  const shadow = host.attachShadow({ mode: 'open' });
  const styleEl = document.createElement('style');
  styleEl.textContent = STYLES;
  shadow.appendChild(styleEl);

  const wrap = document.createElement('div');
  wrap.className = 'wrap';
  wrap.innerHTML = `
    <div class="panel" id="panel">
      <div class="head">
        <span class="title">Aris Request Watcher</span>
        <div class="btns">
          <button id="har">HAR</button>
          <button id="json">JSON</button>
          <button id="close">×</button>
        </div>
      </div>
      <div id="list"></div>
    </div>
    <div class="badge" id="badge">
      <span class="dot" id="dot"></span>
      <span class="stat"><b id="c-total">0</b> <span class="muted">зап.</span></span>
      <span class="stat"><b id="c-err">0</b> <span class="muted">ошб.</span></span>
      <span class="stat"><b id="c-unk">0</b> <span class="muted">неизв.</span></span>
    </div>`;
  shadow.appendChild(wrap);
  document.body.appendChild(host);

  const $ = (id: string) => shadow.getElementById(id)!;
  const panel = $('panel');

  $('badge').addEventListener('click', () => panel.classList.toggle('open'));
  $('close').addEventListener('click', () => panel.classList.remove('open'));
  $('har').addEventListener('click', () => download('aris.har', store.exportHar()));
  $('json').addEventListener('click', () => download('aris-requests.json', store.exportJson()));

  const render = () => {
    $('c-total').textContent = String(store.counters.total);
    $('c-err').textContent = String(store.counters.errors);
    $('c-unk').textContent = String(store.counters.unknownDomains);
    $('dot').className = `dot${store.counters.errors > 0 ? ' err' : ''}`;

    const list = $('list');
    list.innerHTML = '';
    for (const ev of store.recent.slice(0, 40)) {
      list.appendChild(renderRow(ev, cfg, shadow));
    }
  };

  const unsub = store.subscribe(render);
  render();

  return () => {
    unsub();
    host.remove();
  };
}

function statusClass(ev: RequestEvent): string {
  if (ev.error || (ev.status ?? 0) >= 500) return 'err';
  if ((ev.status ?? 0) >= 400) return 'warn';
  return 'ok';
}

function renderRow(ev: RequestEvent, cfg: ResolvedConfig, shadow: ShadowRoot): HTMLElement {
  const row = document.createElement('div');
  row.className = 'row';

  const unknown = ev.direction === 'outgoing' && !ev.domain_known;
  const tags = `${!ev.planned ? '<span class="tag">внеплановый</span>' : ''}${
    unknown ? '<span class="tag unknown">неизвестный домен</span>' : ''
  }`;

  row.innerHTML = `
    <span class="m">${ev.method}</span>
    <span class="u" title="${escapeHtml(ev.url)}">${escapeHtml(ev.host)}${escapeHtml(ev.path)}${tags}</span>
    <span class="s ${statusClass(ev)}">${ev.status ?? 'ERR'}</span>
    <div class="detail">
      <div>⏱ <code>${ev.duration_ms ?? '?'} мс</code> · трейс <code>${(ev.trace_id ?? '').slice(0, 8)}</code></div>
      <div>📍 <code>${escapeHtml(ev.source?.file ?? '?')}:${ev.source?.line ?? '?'}</code>
           ${ev.source?.component ? `· &lt;${escapeHtml(ev.source.component)}&gt;` : ''}</div>
      <div>🔁 причина: <code>${ev.cause}</code> · функция: <code>${escapeHtml(ev.source?.function ?? '?')}</code></div>
      ${unknown ? '<div><button class="allow" data-host="' + escapeHtml(ev.host) + '">✓ разрешить домен</button></div>' : ''}
    </div>`;

  const detail = row.querySelector('.detail')!;
  row.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('allow')) return;
    detail.classList.toggle('open');
  });

  const allowBtn = row.querySelector('.allow') as HTMLButtonElement | null;
  if (allowBtn) {
    allowBtn.addEventListener('click', async () => {
      allowBtn.textContent = '...';
      const ok = await markAllowed(cfg, allowBtn.dataset.host!);
      allowBtn.textContent = ok ? '✓ разрешен' : '✗ ошибка';
      allowBtn.disabled = ok;
    });
  }
  return row;
}

async function markAllowed(cfg: ResolvedConfig, host: string): Promise<boolean> {
  try {
    const base = cfg.collectorUrl.replace(/\/api\/events\/ingest$/, '');
    const res = await fetch(`${base}/api/rules/allowed-domains`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-aris-api-key': cfg.apiKey },
      body: JSON.stringify({ pattern: host }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function download(name: string, content: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
